import { useCallback, useEffect, useRef } from "react"
import { useTheme, Box, CardMedia, SxProps } from "@mui/material"
import { deepmerge } from "@mui/utils"

import { Carousel, CarouselItem } from "components/Carousel"
import { pushDataLayer } from "utils/googletagmanager"
import { Options } from "@splidejs/splide"
import { SponsoredContentFeature } from "venue-react"

// TODO: Drop this once venue-js's SponsoredContentProperties declares these
// media fields (the CMS already saves them; missing as of venue-js@1.8.0-next.7).
type SponsoredContentMediaProperties = SponsoredContentFeature["properties"] & {
  media_duration?: number
  media_resize_mode?: "contain" | "cover"
}

const DEFAULT_INTERVAL_SEC = 15 // 15 secs

// Durations are authored in seconds (per-item media_duration, or the configured
// fallback); Splide and GTM want milliseconds.
const resolveIntervalMs = (
  mediaDurationSec?: number,
  fallbackSec = DEFAULT_INTERVAL_SEC
) => {
  return (mediaDurationSec ?? fallbackSec) * 1000
}

/** Splide options with `interval` in seconds (AdMediaCarousel converts to ms internally). */
export type AdMediaCarouselOptions = Omit<Options, "interval"> & {
  interval?: number
}

export type AdMediaCarouselProps = {
  data: SponsoredContentFeature[]
  sx?: SxProps
  carouselOptions: AdMediaCarouselOptions
  spacing?: number | string
  componentName?: string
  width?: number
  height?: number
}

export const createCardMediaPropsFromFileField = (
  media: SponsoredContentFeature["properties"]["media"]
) => {
  if (!media) return {}

  const isVideo = /^video\//.test(media.mime)
  const src = media.url
  return {
    src,
    component: isVideo ? "video" : "img",
    ...(isVideo ? { autoPlay: true, muted: true, loop: true } : {}),
  }
}

export const AdMediaCarousel = ({
  data = [],
  sx = {},
  carouselOptions = {},
  spacing = "24px",
  componentName = `VuiAdMediaCarouselFooter`,
  width = 1080,
  height = 480,
  ...props
}: AdMediaCarouselProps) => {
  const theme = useTheme()
  const styleOverrides = theme.components[componentName]?.styleOverrides || {}
  const { root, carousel, carouselItem } = styleOverrides
  const dataRef = useRef(data)
  useEffect(() => {
    dataRef.current = data
  }, [data])

  const handleActive = useCallback(
    (_splide, slide) => {
      const content = dataRef.current[slide.index]
      if (!content) return
      const properties = content.properties as SponsoredContentMediaProperties

      const duration = resolveIntervalMs(
        properties.media_duration,
        carouselOptions.interval
      )
      pushDataLayer({
        event: "display_sponsored_content",
        sponsor_content_id: content.id,
        sponsor_content_name: properties.name.en,
        sponsor_content_media_mime: properties.media?.mime,
        sponsor_content_media_src: properties.media?.url,
        sponsor_content_placement_type: properties.placement_type,
        sponsor_content_placement_width: width,
        sponsor_content_placement_height: height,
        sponsor_content_media_duration: duration,
        /**
         * Ideas for future update
         *  - Add venue, level, zone parameter
         * - Add "Ad owner" to help group result by client
         * - Add hour_of_day to show report by hours
         * - Add content_position - index in the carousel
         */
      })
    },
    [width, height, carouselOptions.interval]
  )
  return (
    <Box
      className="AdMediaCarousel-root"
      sx={deepmerge(root, { width, height, ...sx })}
      {...props}
    >
      <Carousel
        key={data.length}
        options={{
          type: "loop",
          padding: spacing,
          gap: spacing,
          pagination: false,
          drag: false,
          autoplay: true,
          pauseOnFocus: false,
          pauseOnHover: false,
          ...carouselOptions,
        }}
        sx={carousel}
        onActive={handleActive}
      >
        {data.map(({ id, properties }) => {
          const { media, media_resize_mode, media_duration } =
            properties as SponsoredContentMediaProperties
          // Item-specific duration → project config → default
          const slideInterval = resolveIntervalMs(
            media_duration,
            carouselOptions.interval
          )
          const objectFit = media_resize_mode ?? "contain"
          const mediaProps = createCardMediaPropsFromFileField(media)
          return (
            <CarouselItem
              key={`slide-${id}`}
              sx={carouselItem}
              data-splide-interval={slideInterval}
            >
              <CardMedia sx={{ ...carouselItem, objectFit }} {...mediaProps} />
            </CarouselItem>
          )
        })}
      </Carousel>
    </Box>
  )
}
